import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  bulkUpdateClientImportRows,
  aiTranslateClientImport,
  commitClientImportJob,
  createClientImportJob,
  downloadClientImportTemplate,
  listClientImportRows,
  previewClientImportJob,
  rollbackImportJob,
} from "../../api/clientImport";
import { useAuth } from "../../context/AuthContext";
import { getCurrentSalonId, getCurrentSalonName } from "../../utils/currentSalon";
import {
  MODAL_TEXT,
  LABEL_TEXT,
  FIELD_CLASS,
  BTN_PRIMARY,
  BTN_OUTLINE,
  BTN_DANGER,
} from "../../ui/modalStyles";
import SBINModal from "../modals/SBINModal";
import { formatNANP } from "../../utils/phone";
import InfoTooltip from "../common/InfoTooltip";
import ReportHelpCard from "../reports/ReportHelpCard";

const STEP_LABELS = [
  "1. Upload",
  "2. AI CSV Translator",
  "3. Column Mapping",
  "4. Preview",
  "5. Commit",
];

const FIELD_DEFS = [
  { key: "full_name", label: "Full Name" },
  { key: "first_name", label: "First Name" },
  { key: "last_name", label: "Last Name" },
  { key: "phone", label: "Phone (required)" },
  { key: "email", label: "Email" },
  { key: "notes", label: "Notes" },
  { key: "birthday", label: "Birthday (YYYY-MM-DD)" },
  { key: "preferred_contact", label: "Preferred Contact" },
  { key: "receive_reminders", label: "Appointment Reminders (Yes/No)" },
  { key: "receive_marketing", label: "Marketing (Yes/No)" },
  { key: "address1", label: "Address 1" },
  { key: "address2", label: "Address 2" },
  { key: "city", label: "City" },
  { key: "state_province", label: "State/Province" },
  { key: "postal_code", label: "Postal Code" },
];

const SYNONYMS = {
  first_name: ["first", "first name", "firstname", "given name"],
  last_name: ["last", "last name", "lastname", "surname"],
  full_name: ["name", "full name", "client name"],
  phone: ["phone", "mobile", "cell", "telephone", "phone number"],
  email: ["email", "e-mail", "email address"],
  notes: ["note", "notes", "comment", "comments"],
  birthday: ["dob", "birth date", "birthday", "date of birth"],
  preferred_contact: ["preferred contact", "contact preference", "preferred method"],
  receive_reminders: ["reminders", "appointment reminders", "appointment reminder"],
  receive_marketing: ["marketing", "promotions", "promotion", "marketing opt in"],
  address1: ["address", "address1", "address 1", "addr", "street", "street address"],
  address2: ["address2", "address 2", "unit", "suite", "apt", "apartment"],
  city: ["city", "town"],
  state_province: ["state", "province", "state/province", "state province"],
  postal_code: ["zip", "postal", "postal code", "zip code", "zipcode", "postcode"],
};

const ISSUE_LABELS = {
  NAME_REQUIRED: "Name required",
  PHONE_REQUIRED: "Phone required",
  EMAIL_INVALID: "Email invalid",
  PHONE_INVALID: "Phone must be 10 digits",
  BIRTHDAY_INVALID: "Birthday invalid",
  BOOLEAN_INVALID: "Invalid Yes/No",
  DUPLICATE_IN_FILE: "Duplicate in file",
  CONFLICTING_MATCHES: "Conflicting matches",
  MULTIPLE_MATCHES: "Multiple matches",
  PHONE_FORMAT_UNUSUAL: "Phone format unusual",
  PHONE_MISSING: "Phone missing",
  MATCH_REQUIRED: "Match required",
  UPDATE_NOT_ALLOWED: "Update not allowed",
  REQUIRED_FIELDS: "Missing required fields",
  NAME_MISSING: "Name missing",
  REMINDERS_INVALID: "Reminders invalid",
  MARKETING_INVALID: "Marketing invalid",
};

const HELP_SECTIONS = [
  {
    id: "upload",
    title: "Step 1: Upload file",
    description: "Download the template, upload your client file, and confirm import options.",
    anchorId: "ci-step-1",
    bullets: [
      "Use CSV or XLSX format.",
      "Confirm salon context before continuing.",
      "Choose update options for existing clients.",
    ],
    tips: [
      "Use template headers for better auto-mapping.",
      "Test with a small file before large imports.",
    ],
  },
  {
    id: "translator",
    title: "Step 2: AI CSV translator",
    description: "Review the AI mapping report and continue when it looks correct.",
    anchorId: "ci-step-2",
    bullets: [
      "Check source row and translated row counts.",
      "Review warnings summary for possible cleanup.",
      "Switch to manual mapping if needed.",
    ],
    tips: ["Use manual mapping when source headers are inconsistent."],
  },
  {
    id: "mapping",
    title: "Step 3: Column mapping",
    description: "Map file columns to client fields before preview.",
    anchorId: "ci-step-3",
    bullets: [
      "Phone and name fields are required.",
      "Leave optional fields unmapped if not provided.",
      "Double-check phone, email, and birthday fields.",
    ],
    tips: ["Map one source column per target field to avoid conflicts."],
  },
  {
    id: "preview",
    title: "Step 4: Preview and fix",
    description: "Review create/update/skip actions and resolve row issues.",
    anchorId: "ci-step-4",
    bullets: [
      "Use filters to review errors and warnings first.",
      "Pick the correct match for UPDATE rows.",
      "Set unresolved rows to SKIP when needed.",
    ],
    tips: ["Resolve all blocking errors before continuing."],
  },
  {
    id: "commit",
    title: "Step 5: Commit import",
    description: "Commit the import and review final counts and rollback options.",
    anchorId: "ci-step-5",
    bullets: [
      "Commit applies create and update actions.",
      "Download error CSV if rows fail.",
      "Use Undo Import only when immediate rollback is required.",
    ],
    tips: ["Review import history after commit for audit tracking."],
  },
];

const TROUBLESHOOTING_ITEMS = [
  {
    title: "Upload does not continue",
    body: "Check file format, salon context, and required values.",
  },
  {
    title: "Rows show unresolved errors",
    body: "Set row action to SKIP or select a valid match for UPDATE rows.",
  },
  {
    title: "Commit button blocked",
    body: "Resolve preview errors first, then continue to commit.",
  },
];

const TOOLTIPS = {
  upload_file: "Upload CSV or XLSX with your client rows.",
  salon: "Import updates only this salon's client records.",
  ai_translator: "Converts source headers to Ellyanna client fields automatically.",
  allow_updates: "Lets matched existing client records be updated.",
  overwrite_notes: "Replaces existing notes/preferences during updates.",
  mapping: "Map each source column to the right client field.",
  action: "Choose create, update, or skip for each row.",
  match: "Required when row action is UPDATE.",
  issues: "Rows with errors must be fixed or skipped before commit.",
  commit: "Final step that applies import changes.",
};

const PREVIEW_PAGE_SIZE = 25;

function normalizeHeader(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ");
}

function autoMapHeaders(headers = []) {
  const normalized = headers.map((header) => ({
    raw: header,
    norm: normalizeHeader(header),
  }));
  const used = new Set();
  const mapping = {};

  FIELD_DEFS.forEach((field) => {
    const synonyms = SYNONYMS[field.key] || [];
    const match = normalized.find(
      (header) =>
        !used.has(header.raw) &&
        (synonyms.includes(header.norm) || header.norm === normalizeHeader(field.key))
    );
    if (match) {
      mapping[field.key] = match.raw;
      used.add(match.raw);
    } else {
      mapping[field.key] = "";
    }
  });
  return mapping;
}

function buildPageRange(current, total) {
  if (total <= 7) {
    return Array.from({ length: total }, (_, i) => i + 1);
  }
  const pages = new Set([1, total, current - 1, current, current + 1]);
  const sorted = Array.from(pages)
    .filter((p) => p >= 1 && p <= total)
    .sort((a, b) => a - b);
  const output = [];
  let last = 0;
  for (const page of sorted) {
    if (page - last > 1) {
      output.push("ellipsis");
    }
    output.push(page);
    last = page;
  }
  return output;
}

function issueCode(issue) {
  if (!issue) return "UNKNOWN";
  return typeof issue === "string" ? issue : issue.code || "UNKNOWN";
}

export default function ClientImportWizard({
  embedded = false,
  initialSalonId = null,
  onComplete,
  onClose,
}) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [step, setStep] = useState(1);
  const [selectedSalonId, setSelectedSalonId] = useState(null);
  const [file, setFile] = useState(null);
  const [headers, setHeaders] = useState([]);
  const [jobId, setJobId] = useState("");
  const [mapping, setMapping] = useState({});
  const [useAiTranslator, setUseAiTranslator] = useState(true);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiMappingReport, setAiMappingReport] = useState([]);
  const [aiWarnings, setAiWarnings] = useState([]);
  const [aiStats, setAiStats] = useState(null);
  const [aiUsed, setAiUsed] = useState(false);
  const [manualLoading, setManualLoading] = useState(false);
  const [allowUpdates, setAllowUpdates] = useState(true);
  const [overwriteNotes, setOverwriteNotes] = useState(false);
  const [loading, setLoading] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [commitLoading, setCommitLoading] = useState(false);
  const [error, setError] = useState("");
  const [preview, setPreview] = useState(null);
  const [previewFilter, setPreviewFilter] = useState("all");
  const [previewPage, setPreviewPage] = useState(1);
  const [previewRows, setPreviewRows] = useState([]);
  const [previewRowsLoading, setPreviewRowsLoading] = useState(false);
  const [previewTotalRows, setPreviewTotalRows] = useState(0);
  const [bulkAction, setBulkAction] = useState("skip");
  const [bulkConfirmOpen, setBulkConfirmOpen] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkPending, setBulkPending] = useState(null);
  const [bulkNotice, setBulkNotice] = useState("");
  const [rowOverrides, setRowOverrides] = useState({});
  const [commitResult, setCommitResult] = useState(null);
  const [rollbackModalOpen, setRollbackModalOpen] = useState(false);
  const [rollbackLoading, setRollbackLoading] = useState(false);
  const [rollbackReport, setRollbackReport] = useState(null);
  const [highlightedSectionId, setHighlightedSectionId] = useState("");
  const highlightTimeoutRef = useRef(null);

  const currentSalonId = getCurrentSalonId(user);
  const salonName = getCurrentSalonName(user);
  const hideSalonSelector = true;
  const resolvedSalonId = initialSalonId ?? currentSalonId;
  const isSalonLocked = hideSalonSelector || Boolean(initialSalonId);

  useEffect(() => {
    if (!resolvedSalonId) return;
    setSelectedSalonId(resolvedSalonId);
    setError((prev) => {
      if (
        prev === "Select a salon before importing clients." ||
        prev === "No salon context found. Please log in again."
      ) {
        return "";
      }
      return prev;
    });
  }, [resolvedSalonId]);

  useEffect(() => {
    if (selectedSalonId || resolvedSalonId) return;
    if (isSalonLocked) {
      setError("No salon context found. Please log in again.");
    } else {
      setError("Select a salon before importing clients.");
    }
    const timeout = setTimeout(() => {
      navigate("/my-salons", { replace: true });
    }, 600);
    return () => clearTimeout(timeout);
  }, [selectedSalonId, resolvedSalonId, isSalonLocked, navigate]);

  useEffect(
    () => () => {
      if (highlightTimeoutRef.current) {
        clearTimeout(highlightTimeoutRef.current);
      }
    },
    []
  );

  const optionsPayload = useMemo(
    () => ({
      allow_updates: allowUpdates,
      overwrite_notes: overwriteNotes,
    }),
    [allowUpdates, overwriteNotes]
  );

  useEffect(() => {
    if (step !== 4 || !jobId || !preview) return;
    let isActive = true;
    setPreviewRowsLoading(true);
    setPreviewRows([]);
    setError("");
    listClientImportRows(jobId, {
      page: previewPage,
      limit: PREVIEW_PAGE_SIZE,
      filter: previewFilter,
    })
      .then((data) => {
        if (!isActive) return;
        const rows = Array.isArray(data?.rows) ? data.rows : [];
        setPreviewRows(rows);
        setPreviewTotalRows(Number(data?.total_rows || 0));
      })
      .catch((err) => {
        if (!isActive) return;
        const detail = err?.response?.data?.detail;
        setError(
          typeof detail === "string"
            ? detail
            : detail?.message || "Unable to load preview rows."
        );
        setPreviewRows([]);
        setPreviewTotalRows(0);
      })
      .finally(() => {
        if (isActive) {
          setPreviewRowsLoading(false);
        }
      });
    return () => {
      isActive = false;
    };
  }, [step, jobId, preview, previewFilter, previewPage]);

  useEffect(() => {
    if (!bulkNotice) return;
    const timeout = setTimeout(() => setBulkNotice(""), 2500);
    return () => clearTimeout(timeout);
  }, [bulkNotice]);

  useEffect(() => {
    if (!allowUpdates && bulkAction === "update") {
      setBulkAction("skip");
    }
  }, [allowUpdates, bulkAction]);

  const previewAllRows = useMemo(() => preview?.rows || [], [preview]);
  const fieldLabelByKey = useMemo(() => {
    const labels = {};
    FIELD_DEFS.forEach((field) => {
      labels[field.key] = field.label;
    });
    return labels;
  }, []);
  const aiWarningsSummary = useMemo(() => {
    const counts = {};
    (aiWarnings || []).forEach((warning) => {
      const reason = warning?.reason || "Other";
      counts[reason] = (counts[reason] || 0) + 1;
    });
    return Object.entries(counts).sort((a, b) => b[1] - a[1]);
  }, [aiWarnings]);

  const pageInfo = useMemo(() => {
    const total = previewTotalRows || 0;
    const startIndex =
      total === 0 ? 0 : (previewPage - 1) * PREVIEW_PAGE_SIZE + 1;
    const endIndex = Math.min(
      total,
      (previewPage - 1) * PREVIEW_PAGE_SIZE + previewRows.length
    );
    return { total, startIndex, endIndex };
  }, [previewTotalRows, previewPage, previewRows.length]);

  const mappingErrors = useMemo(() => {
    const errors = [];
    const hasPhone = mapping.phone && mapping.phone.trim();
    const hasFullName = mapping.full_name && mapping.full_name.trim();
    const hasFirst = mapping.first_name && mapping.first_name.trim();
    const hasLast = mapping.last_name && mapping.last_name.trim();
    if (!hasPhone) errors.push("Map Phone");
    if (!(hasFullName || (hasFirst && hasLast))) {
      errors.push("Map Full Name or First + Last");
    }
    return errors;
  }, [mapping]);

  const mappingIsValid = mappingErrors.length === 0;

  const unresolvedPreviewErrors = useMemo(() => {
    return previewAllRows.some((row) => {
      const errors = row.errors || [];
      const override = rowOverrides[row.row_id];
      const action =
        (
          override?.action ||
          row.override_action ||
          row.proposed_action ||
          "CREATE"
        ).toUpperCase();
      const matchId = override?.match_client_id || row.match_client_id;
      if (action === "UPDATE" && !matchId) return true;
      if (errors.length === 0) return false;
      if (action === "SKIP") return false;
      return true;
    });
  }, [previewAllRows, rowOverrides]);

  const scrollToSection = useCallback((id) => {
    const target = typeof document !== "undefined" ? document.getElementById(id) : null;
    if (!target) return false;
    target.scrollIntoView({ behavior: "smooth", block: "start" });
    return true;
  }, []);

  const jumpToSectionWithHighlight = useCallback(
    (id) => {
      const stepByAnchor = {
        "ci-step-1": 1,
        "ci-step-2": 2,
        "ci-step-3": 3,
        "ci-step-4": 4,
        "ci-step-5": 5,
      };
      const targetStep = stepByAnchor[id];
      const runHighlight = () => {
        const jumped = scrollToSection(id);
        if (!jumped) return;
        setHighlightedSectionId(id);
        if (highlightTimeoutRef.current) {
          clearTimeout(highlightTimeoutRef.current);
        }
        highlightTimeoutRef.current = setTimeout(() => {
          setHighlightedSectionId((current) => (current === id ? "" : current));
        }, 1500);
      };

      if (targetStep && targetStep !== step) {
        setStep(targetStep);
        setTimeout(runHighlight, 120);
        return;
      }
      runHighlight();
    },
    [scrollToSection, step]
  );

  const getSectionClassName = useCallback(
    (id, baseClassName) =>
      `${baseClassName} ${
        highlightedSectionId === id
          ? "ring-2 ring-rose-300 ring-offset-2 transition-shadow duration-200"
          : ""
      }`,
    [highlightedSectionId]
  );

  const handleDownloadTemplate = async () => {
    setError("");
    try {
      const blob = await downloadClientImportTemplate();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "client_import_template.csv";
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError("Unable to download template.");
    }
  };

  const resetImportState = () => {
    setPreview(null);
    setPreviewFilter("all");
    setRowOverrides({});
    setCommitResult(null);
    setRollbackReport(null);
    setPreviewPage(1);
    setPreviewRows([]);
    setPreviewTotalRows(0);
    setBulkAction("skip");
    setBulkConfirmOpen(false);
    setBulkPending(null);
    setBulkNotice("");
    setPreviewLoading(false);
    setPreviewRowsLoading(false);
    setCommitLoading(false);
    setRollbackLoading(false);
  };

  const resetAiState = () => {
    setAiMappingReport([]);
    setAiWarnings([]);
    setAiStats(null);
    setAiUsed(false);
  };

  const buildIdentityMapping = () =>
    FIELD_DEFS.reduce((acc, field) => {
      acc[field.key] = field.key;
      return acc;
    }, {});

  const startManualImport = async () => {
    const data = await createClientImportJob({
      file,
      salon_id: selectedSalonId,
    });
    setJobId(data.job_id);
    const nextHeaders = Array.isArray(data.headers) ? data.headers : [];
    setHeaders(nextHeaders);
    setMapping(autoMapHeaders(nextHeaders));
    resetImportState();
    resetAiState();
    setStep(3);
  };

  const runAiTranslate = async () => {
    setAiLoading(true);
    resetAiState();
    try {
      const data = await aiTranslateClientImport({
        file,
        salon_id: selectedSalonId,
      });
      const templateHeaders = Array.isArray(data?.template_headers)
        ? data.template_headers
        : FIELD_DEFS.map((field) => field.key);
      setJobId(data.job_id);
      setHeaders(templateHeaders);
      setMapping(buildIdentityMapping());
      setAiMappingReport(
        Array.isArray(data?.mapping_report) ? data.mapping_report : []
      );
      setAiWarnings(Array.isArray(data?.warnings) ? data.warnings : []);
      setAiStats(data?.stats || null);
      resetImportState();
      setAiUsed(true);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(
        typeof detail === "string"
          ? detail
          : detail?.message || "Unable to translate file with AI."
      );
      setAiUsed(false);
    } finally {
      setAiLoading(false);
    }
  };

  const handleUpload = async () => {
    if (!file || !selectedSalonId) return;
    setError("");
    setLoading(true);
    resetImportState();
    try {
      if (useAiTranslator) {
        setStep(2);
        await runAiTranslate();
      } else {
        await startManualImport();
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(
        typeof detail === "string"
          ? detail
          : detail?.message || "Unable to upload file."
      );
    } finally {
      setLoading(false);
    }
  };

  const handlePreview = async () => {
    if (!mappingIsValid || !jobId) return;
    setPreviewLoading(true);
    setError("");
    try {
      const data = await previewClientImportJob(jobId, {
        mapping,
        options: optionsPayload,
      });
      setPreview(data);
      setRowOverrides({});
      setPreviewPage(1);
      setPreviewFilter("all");
      setPreviewRows([]);
      setPreviewTotalRows(0);
      setBulkAction("skip");
      setBulkConfirmOpen(false);
      setBulkPending(null);
      setBulkNotice("");
      setStep(4);
      setRollbackReport(null);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(
        typeof detail === "string"
          ? detail
          : detail?.message || "Unable to preview import."
      );
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleAiContinue = async () => {
    if (!jobId) return;
    await handlePreview();
  };

  const handleManualMapping = async () => {
    if (!file || !selectedSalonId) return;
    setManualLoading(true);
    setError("");
    try {
      await startManualImport();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(
        typeof detail === "string"
          ? detail
          : detail?.message || "Unable to switch to manual mapping."
      );
    } finally {
      setManualLoading(false);
    }
  };

  const handleCommit = async () => {
    if (!jobId) return;
    setCommitLoading(true);
    setError("");
    try {
      const overrides = Object.entries(rowOverrides).map(([rowId, data]) => ({
        row_id: rowId,
        action: data.action,
        match_client_id: data.match_client_id || null,
      }));
      const data = await commitClientImportJob(jobId, {
        options: optionsPayload,
        overrides,
      });
      setCommitResult(data);
      setRollbackReport(null);
      if (typeof onComplete === "function") {
        onComplete(data);
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(
        typeof detail === "string"
          ? detail
          : detail?.message || "Unable to commit import."
      );
    } finally {
      setCommitLoading(false);
    }
  };

  const handleOverrideAction = (rowId, action) => {
    setRowOverrides((prev) => ({
      ...prev,
      [rowId]: {
        ...prev[rowId],
        action,
      },
    }));
  };

  const handleOverrideMatch = (rowId, matchClientId) => {
    setRowOverrides((prev) => ({
      ...prev,
      [rowId]: {
        ...prev[rowId],
        match_client_id: matchClientId ? Number(matchClientId) : null,
      },
    }));
  };

  const handleRollback = async () => {
    if (!commitResult?.job_id) return;
    setRollbackLoading(true);
    setError("");
    try {
      const data = await rollbackImportJob(commitResult.job_id);
      setRollbackReport(data);
      setRollbackModalOpen(false);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(
        typeof detail === "string"
          ? detail
          : detail?.message || "Unable to roll back this import."
      );
    } finally {
      setRollbackLoading(false);
    }
  };

  const previewFilterOptions = [
    { key: "all", label: "All", count: preview?.summary?.total_rows ?? 0 },
    { key: "errors", label: "Errors", count: preview?.summary?.errors ?? 0 },
  ];

  const previewFilterLabel =
    previewFilterOptions.find((option) => option.key === previewFilter)?.label ||
    "All";

  const bulkActionOptions = [
    { value: "skip", label: "Skip" },
    { value: "create", label: "Create" },
    ...(allowUpdates ? [{ value: "update", label: "Update" }] : []),
  ];

  const handlePreviewFilterChange = (value) => {
    setPreviewFilter(value);
    setPreviewPage(1);
  };

  const getEffectiveAction = (row, overrides = rowOverrides) => {
    const override = overrides[row.row_id];
    return (
      override?.action ||
      row.override_action ||
      row.proposed_action ||
      "CREATE"
    ).toUpperCase();
  };

  const rowHasIssueCode = (row, code) => {
    return (row.errors || []).some((issue) => issueCode(issue) === code);
  };

  const matchesPreviewFilter = (row, filterValue, overrides = rowOverrides) => {
    const normalized = (filterValue || "all").toLowerCase();
    if (normalized === "errors") {
      return (row.errors || []).length > 0;
    }
    if (normalized === "issues") {
      return (row.errors || []).length > 0 || (row.warnings || []).length > 0;
    }
    if (normalized === "duplicates") {
      return rowHasIssueCode(row, "DUPLICATE_IN_FILE");
    }
    if (normalized === "skips") {
      return getEffectiveAction(row, overrides) === "SKIP";
    }
    return true;
  };

  const openBulkConfirm = (actionValue) => {
    setBulkPending({
      action: actionValue,
      filter: previewFilter,
      count: previewTotalRows,
    });
    setBulkConfirmOpen(true);
  };

  const applyBulkAction = async () => {
    if (!jobId || !bulkPending) return;
    setBulkBusy(true);
    setError("");
    try {
      const payload = {
        filter: bulkPending.filter,
        action: bulkPending.action,
        overwrite: true,
      };
      const data = await bulkUpdateClientImportRows(jobId, payload);
      const updatedCount = Number(data?.updated_count || 0);

      if (data?.summary) {
        setPreview((prev) =>
          prev ? { ...prev, summary: { ...prev.summary, ...data.summary } } : prev
        );
      }

      const overrideIds = new Set();
      const nextPreviewRows = previewAllRows.map((row) => {
        if (matchesPreviewFilter(row, bulkPending.filter)) {
          overrideIds.add(row.row_id);
          return {
            ...row,
            override_action: bulkPending.action.toUpperCase(),
          };
        }
        return row;
      });
      setPreview((prev) =>
        prev ? { ...prev, rows: nextPreviewRows } : prev
      );
      if (overrideIds.size > 0) {
        setRowOverrides((prevOverrides) => {
          const nextOverrides = { ...prevOverrides };
          overrideIds.forEach((rowId) => {
            delete nextOverrides[rowId];
          });
          return nextOverrides;
        });
      }

      setPreviewRowsLoading(true);
      const refreshed = await listClientImportRows(jobId, {
        page: previewPage,
        limit: PREVIEW_PAGE_SIZE,
        filter: previewFilter,
      });
      setPreviewRows(Array.isArray(refreshed?.rows) ? refreshed.rows : []);
      setPreviewTotalRows(Number(refreshed?.total_rows || 0));

      setBulkNotice(
        `Applied ${bulkPending.action.toUpperCase()} to ${updatedCount} rows.`
      );
      setBulkConfirmOpen(false);
      setBulkPending(null);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(
        typeof detail === "string"
          ? detail
          : detail?.message || "Unable to apply bulk action."
      );
    } finally {
      setBulkBusy(false);
      setPreviewRowsLoading(false);
    }
  };

  const renderPagination = (page, totalRows, setPage) => {
    const totalPages = Math.max(1, Math.ceil(totalRows / PREVIEW_PAGE_SIZE));
    const pages = buildPageRange(page, totalPages);
    return (
      <div className="flex items-center gap-1 text-sm">
        <button
          type="button"
          onClick={() => setPage(Math.max(1, page - 1))}
          disabled={page <= 1}
          className="h-10 w-10 rounded-full border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 disabled:opacity-40 disabled:hover:bg-white"
        >
          {"<"}
        </button>
        {pages.map((entry, idx) =>
          entry === "ellipsis" ? (
            <span key={`ellipsis-${idx}`} className="px-2 text-slate-400">
              ...
            </span>
          ) : (
            <button
              key={entry}
              type="button"
              onClick={() => setPage(entry)}
              className={`h-10 w-10 rounded-full border text-sm md:text-base ${
                entry === page
                  ? "border-slate-900 bg-slate-900 text-white"
                  : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
              }`}
            >
              {entry}
            </button>
          )
        )}
        <button
          type="button"
          onClick={() => setPage(Math.min(totalPages, page + 1))}
          disabled={page >= totalPages}
          className="h-10 w-10 rounded-full border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 disabled:opacity-40 disabled:hover:bg-white"
        >
          {">"}
        </button>
      </div>
    );
  };

  const stepHeader = (
    <div className="flex flex-wrap gap-2">
      {STEP_LABELS.map((label, idx) => {
        const isActive = step === idx + 1;
        return (
          <span
            key={label}
            className={`h-9 px-4 rounded-full text-sm md:text-base font-semibold flex items-center ${
              isActive
                ? "bg-slate-900 text-white"
                : "border border-slate-200 text-slate-600"
            }`}
          >
            {label}
          </span>
        );
      })}
    </div>
  );

  const renderStepOne = (
    <div id="ci-step-1" className={getSectionClassName("ci-step-1", "space-y-5")}>
      <div className="flex flex-wrap items-center gap-3">
        <button
          type="button"
          onClick={handleDownloadTemplate}
          className={BTN_OUTLINE}
        >
          Download CSV template
        </button>
        <div className="text-sm md:text-base text-slate-500">
          Upload CSV or XLSX with your client data.
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <div className={`inline-flex items-center gap-2 ${LABEL_TEXT}`}>
            Upload File
            <InfoTooltip text={TOOLTIPS.upload_file} label="Upload File" />
          </div>
          <input
            type="file"
            accept=".csv,.xlsx"
            onChange={(event) => setFile(event.target.files?.[0] || null)}
            className={`mt-1 w-full ${FIELD_CLASS}`}
          />
        </div>
        <div>
          <div className={`inline-flex items-center gap-2 ${LABEL_TEXT}`}>
            Salon
            <InfoTooltip text={TOOLTIPS.salon} label="Salon" />
          </div>
          <div className="mt-1 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm md:text-base text-slate-700">
            Salon: {salonName || "Unknown salon"}
            {selectedSalonId ? ` (ID ${selectedSalonId})` : ""}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <label className="flex items-center gap-3 text-sm md:text-base text-slate-700">
          <input
            type="checkbox"
            checked={useAiTranslator}
            onChange={(event) => setUseAiTranslator(event.target.checked)}
          />
          Use AI translator (recommended)
          <InfoTooltip text={TOOLTIPS.ai_translator} label="AI CSV Translator" />
        </label>
        <label className="flex items-center gap-3 text-sm md:text-base text-slate-700">
          <input
            type="checkbox"
            checked={allowUpdates}
            onChange={(event) => setAllowUpdates(event.target.checked)}
          />
          Allow updating existing clients
          <InfoTooltip text={TOOLTIPS.allow_updates} label="Allow Updates" />
        </label>
        <label className="flex items-center gap-3 text-sm md:text-base text-slate-700">
          <input
            type="checkbox"
            checked={overwriteNotes}
            onChange={(event) => setOverwriteNotes(event.target.checked)}
          />
          Overwrite existing notes/preferences on update
          <InfoTooltip text={TOOLTIPS.overwrite_notes} label="Overwrite Notes" />
        </label>
      </div>

      <div className="flex justify-end">
        <button
          type="button"
          onClick={handleUpload}
          className={BTN_PRIMARY}
          disabled={loading || !file || !selectedSalonId}
        >
          {loading ? "Uploading..." : "Continue"}
        </button>
      </div>
    </div>
  );

  const renderStepTwo = (
    <div id="ci-step-2" className={getSectionClassName("ci-step-2", "space-y-5")}>
      {aiLoading && (
        <div className="rounded-2xl border border-slate-200 bg-white p-4">
          <div className="flex items-center gap-3 text-sm md:text-base text-slate-700">
            <div className="h-5 w-5 rounded-full border-2 border-slate-200 border-t-slate-700 animate-spin" />
            Translating your file with AI...
          </div>
          <div className="mt-2 text-xs md:text-sm text-slate-500">
            Large files can take a few moments. We will create an import-ready file
            that matches the Ellyanna template.
          </div>
        </div>
      )}

      {!aiLoading && aiMappingReport.length > 0 && (
        <>
          <div className="rounded-2xl border border-slate-200 bg-white p-4 space-y-3">
            <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              Mapping report
            </div>
            {aiStats && (
              <div className="flex flex-wrap gap-3 text-sm md:text-base text-slate-700">
                <span>Source rows: {aiStats.source_row_count ?? 0}</span>
                <span>Translated rows: {aiStats.translated_row_count ?? 0}</span>
              </div>
            )}
            <div className="overflow-hidden rounded-2xl border border-slate-200">
              <table className="min-w-full">
                <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="px-3 py-2">Ellyanna Field</th>
                    <th className="px-3 py-2">Source Columns</th>
                    <th className="px-3 py-2">Notes</th>
                    <th className="px-3 py-2">Confidence</th>
                  </tr>
                </thead>
                <tbody className="text-sm md:text-base text-slate-700">
                  {aiMappingReport.map((entry, idx) => {
                    const confidence = Number(entry?.confidence);
                    const confidenceLabel = Number.isFinite(confidence)
                      ? `${Math.round(confidence * 100)}%`
                      : "-";
                    return (
                      <tr
                        key={`${entry?.sbin_field || "field"}-${idx}`}
                        className="border-t border-slate-100 hover:bg-slate-50"
                      >
                        <td className="px-3 py-2 font-semibold text-slate-900">
                          {fieldLabelByKey[entry?.sbin_field] || entry?.sbin_field}
                        </td>
                        <td className="px-3 py-2">
                          {(entry?.source_columns || []).join(", ") || "-"}
                        </td>
                        <td className="px-3 py-2">{entry?.notes || "-"}</td>
                        <td className="px-3 py-2">{confidenceLabel}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {aiWarningsSummary.length > 0 && (
            <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm md:text-base text-amber-700">
              <div className="text-xs font-semibold uppercase tracking-wide text-amber-700">
                Warnings summary
              </div>
              <div className="mt-1">Total warnings: {aiWarnings.length}</div>
              <div className="mt-2 flex flex-wrap gap-2">
                {aiWarningsSummary.slice(0, 4).map(([reason, count]) => (
                  <span
                    key={reason}
                    className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-700"
                  >
                    {count} {ISSUE_LABELS[reason] || reason}
                  </span>
                ))}
                {aiWarningsSummary.length > 4 && (
                  <span className="text-xs text-amber-700">
                    +{aiWarningsSummary.length - 4} more
                  </span>
                )}
              </div>
            </div>
          )}
        </>
      )}

      {!aiLoading && aiMappingReport.length === 0 && (
        <div className="rounded-2xl border border-slate-200 bg-white p-4 text-sm md:text-base text-slate-600">
          AI translation is not ready. Continue with manual mapping to proceed.
        </div>
      )}

      <div className="flex flex-wrap items-center justify-between gap-2">
        <button
          type="button"
          onClick={() => setStep(1)}
          className={BTN_OUTLINE}
          disabled={aiLoading || manualLoading}
        >
          Back
        </button>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={handleManualMapping}
            className={BTN_OUTLINE}
            disabled={manualLoading || aiLoading}
          >
            {manualLoading ? "Switching..." : "Manual Mapping Instead"}
          </button>
          <button
            type="button"
            onClick={handleAiContinue}
            className={BTN_PRIMARY}
            disabled={aiLoading || aiMappingReport.length === 0 || previewLoading}
          >
            {previewLoading ? "Validating..." : "Continue"}
          </button>
        </div>
      </div>
    </div>
  );

  const renderStepThree = (
    <div id="ci-step-3" className={getSectionClassName("ci-step-3", "space-y-5")}>
      <div className="text-sm md:text-base text-slate-600">
        Detected {headers.length} columns from the file. Map them to Ellyanna fields.
      </div>
      <div className="inline-flex items-center gap-2 text-sm md:text-base text-slate-600">
        Column mapping
        <InfoTooltip text={TOOLTIPS.mapping} label="Column Mapping" />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {FIELD_DEFS.map((field) => (
          <div key={field.key}>
            <div className={LABEL_TEXT}>{field.label}</div>
            <select
              value={mapping[field.key] || ""}
              onChange={(event) =>
                setMapping((prev) => ({
                  ...prev,
                  [field.key]: event.target.value,
                }))
              }
              className={`mt-1 w-full ${FIELD_CLASS}`}
            >
              <option value="">Not mapped</option>
              {headers.map((header) => (
                <option key={`${field.key}-${header}`} value={header}>
                  {header}
                </option>
              ))}
            </select>
          </div>
        ))}
      </div>

      {mappingErrors.length > 0 && (
        <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm md:text-base text-rose-700">
          {`Please resolve: ${mappingErrors.join(", ")}.`}
        </div>
      )}

      <div className="flex items-center justify-between">
        <button
          type="button"
          onClick={() => setStep(1)}
          className={BTN_OUTLINE}
        >
          Back
        </button>
        <button
          type="button"
          onClick={handlePreview}
          className={BTN_PRIMARY}
          disabled={!mappingIsValid || previewLoading}
        >
          {previewLoading ? "Validating..." : "Continue"}
        </button>
      </div>
    </div>
  );

  const renderStepFour = (
    <div id="ci-step-4" className={getSectionClassName("ci-step-4", "space-y-6")}>
      <div className="grid grid-cols-2 md:grid-cols-7 gap-3">
        {[
          ["Total rows", preview?.summary?.total_rows],
          ["Creates", preview?.summary?.creates],
          ["Updates", preview?.summary?.updates],
          ["Skips", preview?.summary?.skips],
          ["Errors", preview?.summary?.errors],
          ["Duplicate in file", preview?.summary?.duplicate_in_file],
          ["Conflicts", preview?.summary?.conflicts],
        ].map(([label, value]) => (
          <div
            key={label}
            className="rounded-2xl border border-slate-200 bg-white p-4"
          >
            <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              {label}
            </div>
            <div className="mt-1 text-lg font-semibold text-slate-900">
              {value ?? 0}
            </div>
          </div>
        ))}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        {previewFilterOptions.map((option) => {
          const isActive = previewFilter === option.key;
          return (
            <button
              key={option.key}
              type="button"
              onClick={() => handlePreviewFilterChange(option.key)}
              className={`h-9 px-4 rounded-full border text-sm font-semibold ${
                isActive
                  ? "border-slate-900 bg-slate-900 text-white"
                  : "border-slate-200 text-slate-600 hover:bg-slate-50"
              }`}
            >
              {option.label} ({option.count})
            </button>
          );
        })}
      </div>
      <div className="flex flex-wrap items-center gap-3 justify-between">
        <div className="flex flex-wrap items-center gap-2">
          <div>
            <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              Bulk actions
            </div>
            <select
              value={bulkAction}
              onChange={(event) => setBulkAction(event.target.value)}
              className={`mt-1 ${FIELD_CLASS}`}
              disabled={bulkBusy || previewTotalRows === 0}
            >
              {bulkActionOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          <button
            type="button"
            onClick={() => openBulkConfirm(bulkAction)}
            className={BTN_OUTLINE}
            disabled={bulkBusy || previewTotalRows === 0}
          >
            Apply
          </button>
          <span className="text-xs md:text-sm text-slate-500">
            Applies to: current filter ({previewFilterLabel})
          </span>
        </div>
        {previewFilter === "errors" && (
          <button
            type="button"
            onClick={() => openBulkConfirm("skip")}
            className={BTN_PRIMARY}
            disabled={bulkBusy || previewTotalRows === 0}
          >
            Skip All Errors
          </button>
        )}
        {previewFilter === "all" && (
          <button
            type="button"
            onClick={() => openBulkConfirm("skip")}
            className={BTN_PRIMARY}
            disabled={bulkBusy || previewTotalRows === 0}
          >
            Skip All
          </button>
        )}
      </div>
      <div className="text-xs md:text-sm text-slate-500">
        Showing {previewRows.length} of {previewTotalRows} rows
      </div>
      {bulkNotice && (
        <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-2 text-sm md:text-base text-emerald-700">
          {bulkNotice}
        </div>
      )}

      <div className="space-y-3">
        {renderPagination(previewPage, previewTotalRows, setPreviewPage)}
        <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
          <table className="min-w-full">
            <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-3 py-2">Row #</th>
                <th className="px-3 py-2">Name</th>
                <th className="px-3 py-2">Phone</th>
                <th className="px-3 py-2">Email</th>
                <th className="px-3 py-2">
                  <span className="inline-flex items-center gap-2">
                    Action
                    <InfoTooltip text={TOOLTIPS.action} label="Action" />
                  </span>
                </th>
                <th className="px-3 py-2">
                  <span className="inline-flex items-center gap-2">
                    Match
                    <InfoTooltip text={TOOLTIPS.match} label="Match" />
                  </span>
                </th>
                <th className="px-3 py-2">
                  <span className="inline-flex items-center gap-2">
                    Issues
                    <InfoTooltip text={TOOLTIPS.issues} label="Issues" />
                  </span>
                </th>
              </tr>
            </thead>
            <tbody className="text-sm md:text-base text-slate-800">
              {previewRows.map((row) => {
                const override = rowOverrides[row.row_id];
                const action =
                  (
                    override?.action ||
                    row.override_action ||
                    row.proposed_action ||
                    "CREATE"
                  ).toUpperCase();
                const hasReview = action === "REVIEW";
                const options = allowUpdates
                  ? ["CREATE", "UPDATE", "SKIP"]
                  : ["CREATE", "SKIP"];
                const matchCandidates = row.match_candidates || [];
                const matchId = override?.match_client_id || row.match_client_id || "";
                return (
                  <tr
                    key={row.row_id}
                    className="border-t border-slate-100 hover:bg-slate-50"
                  >
                    <td className="px-3 py-2">{row.row_number}</td>
                    <td className="px-3 py-2 font-semibold text-slate-900">
                      {row.full_name || "-"}
                    </td>
                    <td className="px-3 py-2">
                      {row.phone ? formatNANP(row.phone) : "-"}
                    </td>
                    <td className="px-3 py-2">{row.email || "-"}</td>
                    <td className="px-3 py-2">
                      <select
                        value={hasReview ? "REVIEW" : action}
                        onChange={(event) =>
                          handleOverrideAction(row.row_id, event.target.value)
                        }
                        className={FIELD_CLASS}
                      >
                        {hasReview && (
                          <option value="REVIEW" disabled>
                            Review
                          </option>
                        )}
                        {options.map((opt) => (
                          <option key={opt} value={opt}>
                            {opt}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="px-3 py-2">
                      {action === "UPDATE" ? (
                        matchCandidates.length > 0 ? (
                          <select
                            value={matchId || ""}
                            onChange={(event) =>
                              handleOverrideMatch(row.row_id, event.target.value)
                            }
                            className={FIELD_CLASS}
                          >
                            <option value="">Select client</option>
                            {matchCandidates.map((candidate) => (
                              <option key={candidate.id} value={candidate.id}>
                                {candidate.full_name}{" "}
                                {candidate.phone_masked
                                  ? `(${candidate.phone_masked})`
                                  : candidate.email_masked
                                  ? `(${candidate.email_masked})`
                                  : ""}
                              </option>
                            ))}
                          </select>
                        ) : (
                          <span className="text-slate-500">No match</span>
                        )
                      ) : (
                        <span className="text-slate-500">-</span>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex flex-wrap gap-2">
                        {(row.errors || []).map((issue, idx) => {
                          const code = issueCode(issue);
                          return (
                            <span
                              key={`${row.row_id}-err-${idx}`}
                              className="rounded-full bg-rose-100 px-2 py-0.5 text-xs font-semibold text-rose-700"
                            >
                              {ISSUE_LABELS[code] || code}
                            </span>
                          );
                        })}
                        {(row.warnings || []).map((issue, idx) => {
                          const code = issueCode(issue);
                          return (
                            <span
                              key={`${row.row_id}-warn-${idx}`}
                              className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-700"
                            >
                              {ISSUE_LABELS[code] || code}
                            </span>
                          );
                        })}
                      </div>
                    </td>
                  </tr>
                );
              })}
              {previewRows.length === 0 && !previewRowsLoading && (
                <tr>
                  <td
                    colSpan={7}
                    className="px-3 py-6 text-center text-sm md:text-base text-slate-500"
                  >
                    No preview rows available.
                  </td>
                </tr>
              )}
              {previewRows.length === 0 && previewRowsLoading && (
                <tr>
                  <td
                    colSpan={7}
                    className="px-3 py-6 text-center text-sm md:text-base text-slate-500"
                  >
                    Loading preview rows...
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="text-xs md:text-sm text-slate-500">
          Showing {pageInfo.startIndex} to {pageInfo.endIndex} of {pageInfo.total}{" "}
          entries
        </div>
      </div>

      {unresolvedPreviewErrors && (
        <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm md:text-base text-rose-700">
          Resolve rows with errors before continuing (set them to Skip or select a
          match).
        </div>
      )}

      <div className="flex items-center justify-between">
        <button
          type="button"
          onClick={() => setStep(aiUsed ? 2 : 3)}
          className={BTN_OUTLINE}
        >
          Back
        </button>
        <button
          type="button"
          onClick={() => setStep(5)}
          className={BTN_PRIMARY}
          disabled={unresolvedPreviewErrors}
        >
          Continue
        </button>
      </div>
    </div>
  );

  const renderStepFive = (
    <div id="ci-step-5" className={getSectionClassName("ci-step-5", "space-y-5")}>
      <div className="rounded-2xl border border-slate-200 bg-white p-4">
        <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
          Ready to import
        </div>
        <div className="mt-1 text-sm md:text-base text-slate-700">
          {preview?.summary?.creates ?? 0} creates, {preview?.summary?.updates ?? 0}{" "}
          updates, {preview?.summary?.skips ?? 0} skips.
        </div>
      </div>

      <div className="flex items-center justify-between">
        <button
          type="button"
          onClick={() => setStep(4)}
          className={BTN_OUTLINE}
          disabled={commitLoading}
        >
          Back
        </button>
        <div className="inline-flex items-center gap-2">
          <button
            type="button"
            onClick={handleCommit}
            className={BTN_PRIMARY}
            disabled={commitLoading}
          >
            {commitLoading ? "Importing..." : "Commit Import"}
          </button>
          <InfoTooltip text={TOOLTIPS.commit} label="Commit Import" />
        </div>
      </div>

      {commitResult && (
        <div className="space-y-3">
          <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm md:text-base text-emerald-700">
            Import complete. {commitResult.created_count} created,{" "}
            {commitResult.updated_count} updated, {commitResult.skipped_count} skipped,{" "}
            {commitResult.failed_count} failed.
          </div>

          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => navigate("/manager/manage/client-imports")}
              className={BTN_OUTLINE}
            >
              View Import History
            </button>
            <button
              type="button"
              onClick={() => setRollbackModalOpen(true)}
              className={BTN_DANGER}
              disabled={rollbackLoading || Boolean(rollbackReport)}
            >
              {rollbackLoading ? "Rolling back..." : "Undo Import"}
            </button>
          </div>

          {commitResult.failed_count > 0 && (
            <button
              type="button"
              onClick={() => {
                const rows = commitResult.failures || [];
                const header = "row_number,errors\n";
                const lines = rows.map((row) => {
                  const errors = (row.errors || []).join("; ");
                  return `${row.row_number},"${errors.replaceAll('"', '""')}"\n`;
                });
                const blob = new Blob([header, ...lines], {
                  type: "text/csv;charset=utf-8;",
                });
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement("a");
                link.href = url;
                link.download = "client_import_errors.csv";
                link.click();
                window.URL.revokeObjectURL(url);
              }}
              className={BTN_OUTLINE}
            >
              Download Error Report CSV
            </button>
          )}

          {rollbackReport && (
            <div className="space-y-3 rounded-2xl border border-slate-200 bg-white p-4">
              <div className="text-sm md:text-base text-slate-700">
                Rollback complete. {rollbackReport.deleted_created_clients} created
                clients deleted, {rollbackReport.reverted_updated_clients} updates reverted,{" "}
                {rollbackReport.skipped} skipped.
              </div>
              {(rollbackReport.skipped_details || []).length > 0 && (
                <div className="overflow-hidden rounded-2xl border border-slate-200">
                  <table className="min-w-full">
                    <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                      <tr>
                        <th className="px-3 py-2">Client ID</th>
                        <th className="px-3 py-2">Action</th>
                        <th className="px-3 py-2">Reasons</th>
                        <th className="px-3 py-2">Changed Fields</th>
                      </tr>
                    </thead>
                    <tbody className="text-sm md:text-base text-slate-700">
                      {rollbackReport.skipped_details.map((entry, idx) => (
                        <tr
                          key={`${entry.client_id}-${idx}`}
                          className="border-t border-slate-100 hover:bg-slate-50"
                        >
                          <td className="px-3 py-2">{entry.client_id}</td>
                          <td className="px-3 py-2 uppercase text-xs font-semibold text-slate-500">
                            {entry.action}
                          </td>
                          <td className="px-3 py-2">
                            {(entry.reasons || []).join(", ")}
                          </td>
                          <td className="px-3 py-2">
                            {(entry.changed_fields || []).join(", ") || "-"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {typeof onClose === "function" && (
            <button
              type="button"
              onClick={onClose}
              className={BTN_OUTLINE}
            >
              Done
            </button>
          )}
        </div>
      )}
    </div>
  );

  const wizardBody = (
    <div className={`space-y-6 ${MODAL_TEXT}`}>
      <div id="ci-overview">
        <ReportHelpCard
          title="Help Guide: Import Clients"
          subtitle="Follow these steps to upload, validate, and commit client imports safely."
          sections={HELP_SECTIONS}
          troubleshooting={TROUBLESHOOTING_ITEMS}
          onJumpToSection={jumpToSectionWithHighlight}
        />
      </div>
      {stepHeader}
      {error && (
        <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm md:text-base text-rose-700">
          {typeof error === "string" ? error : "Something went wrong."}
        </div>
      )}
      {step === 1 && renderStepOne}
      {step === 2 && renderStepTwo}
      {step === 3 && renderStepThree}
      {step === 4 && renderStepFour}
      {step === 5 && renderStepFive}
    </div>
  );

  const rollbackModal = (
    <SBINModal
      isOpen={rollbackModalOpen}
      onClose={() => setRollbackModalOpen(false)}
      title="Undo Import"
      size="lg"
      fitContent
    >
      <div className="space-y-4">
        <p className="text-sm md:text-base text-slate-700">
          This will roll back the import. Created clients are deleted only if they
          have no appointments, tickets, or other activity. Updated clients revert
          only if they have not been modified since the import.
        </p>
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={() => setRollbackModalOpen(false)}
            className={BTN_OUTLINE}
            disabled={rollbackLoading}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleRollback}
            className={BTN_DANGER}
            disabled={rollbackLoading}
          >
            {rollbackLoading ? "Rolling back..." : "Confirm Rollback"}
          </button>
        </div>
      </div>
    </SBINModal>
  );

  const bulkConfirmModal = (
    <SBINModal
      isOpen={bulkConfirmOpen}
      onClose={() => {
        setBulkConfirmOpen(false);
        setBulkPending(null);
      }}
      title="Confirm"
      size="md"
      fitContent
    >
      <div className="space-y-4">
        <p className="text-sm md:text-base text-slate-700">
          Apply action "{(bulkPending?.action || "").toUpperCase()}" to{" "}
          {bulkPending?.count ?? 0} rows?
        </p>
        <p className="text-sm md:text-base text-slate-600">
          This will override existing per-row selections.
        </p>
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={() => {
              setBulkConfirmOpen(false);
              setBulkPending(null);
            }}
            className={BTN_OUTLINE}
            disabled={bulkBusy}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={applyBulkAction}
            className={BTN_PRIMARY}
            disabled={bulkBusy}
          >
            {bulkBusy ? "Applying..." : "Apply"}
          </button>
        </div>
      </div>
    </SBINModal>
  );

  if (!embedded) {
    return (
      <>
        {wizardBody}
        {rollbackModal}
        {bulkConfirmModal}
      </>
    );
  }

  return (
    <>
      <div
        className={`rounded-3xl border border-slate-200 bg-white shadow-2xl overflow-hidden ${MODAL_TEXT}`}
      >
        <div className="bg-rose-500 px-6 py-4">
          <h2 className="text-xl md:text-2xl font-semibold text-white">
            Clients
          </h2>
        </div>
        <div className="px-6 py-6 space-y-6">{wizardBody}</div>
      </div>
      {rollbackModal}
      {bulkConfirmModal}
    </>
  );
}
